/**
 * Regras de apresentação do Kardex alinhadas ao portal BI (kardex.php + kardex_api.php).
 * O cálculo de saldo permanece na API SIAL; aqui: filtros pós-API, enriquecimento e colunas.
 */
import { translate } from '../../versa/filter.js';
import { getSialApiHost } from '../api.js';

export const AWAITING_ADDRESSING_CODE = 'S-1';
export const AWAITING_ADDRESSING_LABEL = 'Aguardando Endereçamento';

export const KDX_MOV_KIND = {
	reserva_na_posicao: 'Reserva na posição',
	liberacao_reserva: 'Liberação de reserva',
	liberação_reserva: 'Liberação de reserva',
	entrada: 'Entrada',
	saida: 'Saída',
	saída: 'Saída',
	saida_expedicao: 'Saída expedição',
	saída_expedição: 'Saída expedição',
	transferencia: 'Transferência',
	transferência: 'Transferência',
	ajuste: 'Ajuste',
	ajuste_saldo: 'Ajuste de saldo',
	ressuprimento: 'Ressuprimento',
	consumo: 'Consumo',
	devolucao: 'Devolução',
	devolução: 'Devolução',
	baixa: 'Baixa',
	baixa_saida: 'Baixa saída',
	baixa_saída: 'Baixa saída',
	entrada_para_posicao: 'Entrada para posição',
	entrada_para_posição: 'Entrada para posição',
	transferencia_entre_posicoes: 'Transferência entre posições',
	transferência_entre_posições: 'Transferência entre posições',
	bloqueio: 'Bloqueio',
	bloqueio_estoque: 'Bloqueio de estoque',
	desbloqueio: 'Desbloqueio',
	avaria_fornecedor: 'Avaria Fornecedor',
	avaria_armazem: 'Avaria Armazém',
	avaria_transporte: 'Avaria Transporte',
	quarentena: 'Quarentena',
	vencido: 'Vencidos',
	vencimento_curto: 'Vencimento Curto',
	falta_ibl: 'Falta IBL',
};

export const KDX_PRINT_COLS = [
	{ key: '__num', label: '#', align: 'center' },
	{ key: 'date_time', label: 'Data/Hora', align: 'left' },
	{ key: '__nf_entrada', label: 'NF entrada', align: 'left' },
	{ key: '__req_his', label: 'Ref. cliente / HIS', align: 'left' },
	{ key: '__bloqueio_motivo', label: 'Motivo bloqueio', align: 'left' },
	{ key: '__lote', label: 'Lote', align: 'left' },
	{ key: '__lote_atendido', label: 'Lote atendido', align: 'left' },
	{ key: 'movement_kind', label: 'Tipo de movimento', align: 'left' },
	{ key: '__tipo', label: 'Fluxo (origem→destino)', align: 'center' },
	{ key: 'source', label: 'Origem', align: 'left' },
	{ key: 'target', label: 'Destino', align: 'left' },
	{ key: 'quantity', label: 'Qtd do movimento', align: 'end' },
	{ key: 'balance_qty', label: 'Saldo físico', align: 'end' },
	{ key: '__valor_sinal', label: 'Valor sinal', align: 'end' },
	{ key: 'balance_value', label: 'Saldo valor', align: 'end' },
];

export const KDX_CLIENT_COLS = [
	{ key: '__num', label: '#', align: 'center' },
	{ key: 'date_time', label: 'Data/Hora', align: 'left' },
	{ key: 'movement_kind', label: 'Tipo de movimento', align: 'left' },
	{ key: '__nf_entrada', label: 'Entrada (NF)', align: 'left' },
	{ key: '__req_his', label: 'Saída / consumo (ref.)', align: 'left' },
	{ key: '__bloqueio_motivo', label: 'Motivo bloqueio', align: 'left' },
	{ key: 'quantity', label: 'Qtd do movimento', align: 'end' },
	{ key: 'balance_qty', label: 'Saldo físico', align: 'end' },
];

/** Data final inclusiva (UI) → date_to repassado à API (dia seguinte), como kardex_api.php. */
export function dateToExclusive(dateFimInclusive) {
	if (!dateFimInclusive) return '';
	const d = new Date(`${dateFimInclusive}T12:00:00`);
	if (Number.isNaN(d.getTime())) return '';
	d.setDate(d.getDate() + 1);
	return d.toISOString().slice(0, 10);
}

/** Monta parâmetros GET do Kardex (paridade com portal após proxy). */
export function buildKardexApiParams({
	productId,
	dateFrom,
	dateFim,
	lotText,
	costMode,
	includeDisabled,
	limit,
	offset,
}) {
	const params = {
		product_id: productId,
		date_from: dateFrom,
		date_to: dateToExclusive(dateFim),
		cost_mode: costMode || 'none',
	};
	const lot = String(lotText || '').trim();
	if (lot) {
		if (/^\d+$/.test(lot)) params.lot_id = lot;
		else params.lot_number = lot;
	}
	if (includeDisabled) params.include_disabled = '1';
	if (limit != null) params.limit = String(limit);
	if (offset != null) params.offset = String(offset);
	return params;
}

/** Colunas da grade conforme vista e modo de custo (oculta valores monetários se none). */
export function activeCols(vistaCliente, costMode = 'none') {
	const cols = vistaCliente ? KDX_CLIENT_COLS : KDX_PRINT_COLS;
	if (!vistaCliente && costMode === 'none') {
		return cols.filter((c) => c.key !== '__valor_sinal' && c.key !== 'balance_value');
	}
	return cols;
}

const KDX_REF_COL_KEYS = new Set([
	'__nf_entrada', '__req_his', '__bloqueio_motivo', '__lote', '__lote_atendido',
	'source', 'target', 'movement_kind',
	'__nf_entrada_html', '__req_his_html', '__bloqueio_motivo_html',
]);

function escHtml(s) {
	if (s == null) return '';
	const t = document.createElement('template');
	t.textContent = String(s);
	return t.innerHTML;
}

export function fmtNumberPt(n) {
	if (n === null || n === undefined || n === '') return '—';
	const x = Number(n);
	if (Number.isNaN(x)) return String(n);
	return x.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 6 });
}

export function fmtBRL(n) {
	if (n === null || n === undefined || n === '') return '—';
	const x = Number(n);
	if (Number.isNaN(x)) return String(n);
	return x.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function fmtDateTime(iso) {
	if (!iso) return '—';
	const d = new Date(iso);
	if (!Number.isNaN(d.getTime())) {
		return d.toLocaleString('pt-BR', {
			day: '2-digit', month: '2-digit', year: 'numeric',
			hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
		});
	}
	const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})/);
	if (m) return `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}:${m[6]}`;
	return String(iso);
}

export function fmtDateOnly(v) {
	if (!v) return '—';
	const s = String(v);
	const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
	if (m) return `${m[3]}/${m[2]}/${m[1]}`;
	const d = new Date(s);
	return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString('pt-BR');
}

function movementKindLower(row) {
	return String(row?.movement_kind ?? '').trim().toLowerCase();
}

function movementKindNormalized(row) {
	return movementKindLower(row).normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/** Endereçamento cancelado/estorno de entrada — oculto no extrato (paridade API). */
export function isEntradaStagingNoise(row) {
	if (!row || typeof row !== 'object') return false;
	const st = String(row.source_type ?? row.sourceType ?? '').toUpperCase();
	const tt = String(row.target_type ?? row.targetType ?? '').toUpperCase();
	if (st === 'L' && tt === 'I') return true;
	if (st === 'B' && tt === 'I') return true;
	if (st === 'I' && tt === 'L' && (row.is_disabled || row.isDisabled)) return true;
	const mk = movementKindNormalized(row);
	if (mk.includes('estorno') && mk.includes('entrada')) return true;
	return false;
}

export function filterEntradaStagingNoise(rows) {
	return (rows || []).filter((r) => !isEntradaStagingNoise(r));
}

/** Linhas ocultas na vista cliente (paridade kdxIsRowHiddenForClient). */
export function isRowHiddenForClient(row) {
	if (isEntradaStagingNoise(row)) return true;
	const mk = movementKindNormalized(row);
	if (!mk) return false;
	if (mk.includes('bloqueio') || mk.includes('desbloqueio') || mk.includes('bloquear')) return false;
	if (mk.includes('transfer')) return true;
	if (mk.includes('reserva') && mk.includes('posicao')) return true;
	if (mk.includes('liberacao') && mk.includes('reserva')) return true;
	if (mk.includes('estorno')) return true;
	return false;
}

export function filterRowsForClient(rows) {
	return (rows || []).filter((r) => !isRowHiddenForClient(r));
}

function parseBalanceBr(v) {
	if (v == null || v === '') return 0;
	if (typeof v === 'number' && Number.isFinite(v)) return v;
	const s = String(v).trim().replace(/\s/g, '');
	if (!s) return 0;
	const n = parseFloat(s.replace(/\./g, '').replace(',', '.'));
	return Number.isFinite(n) ? n : 0;
}

function parseQtyDelta(row) {
	if (!row || typeof row !== 'object') return 0;
	const raw = row.qty_delta != null && row.qty_delta !== '' ? row.qty_delta : row.qtyDelta;
	if (raw == null || raw === '') return 0;
	if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
	let s = String(raw).trim().replace(/\s/g, '');
	let neg = false;
	if (s.startsWith('-')) { neg = true; s = s.slice(1); }
	else if (s.startsWith('+')) s = s.slice(1);
	const n = parseFloat(s.replace(/\./g, '').replace(',', '.'));
	if (Number.isFinite(n)) return neg ? -n : n;
	return 0;
}

/** Δ que alimenta o saldo físico: I→L (+), R→O (−); transferências/reservas/estornos = 0. */
function parseBalanceDelta(row) {
	if (!row || typeof row !== 'object') return 0;
	if (row.is_disabled || row.isDisabled) return 0;
	const raw = row.balance_delta != null && row.balance_delta !== ''
		? row.balance_delta
		: row.balanceDelta;
	if (raw != null && raw !== '') {
		if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
		return parseQtyDelta({ qty_delta: raw });
	}
	const st = String(row.source_type || row.sourceType || '').toUpperCase();
	const tt = String(row.target_type || row.targetType || '').toUpperCase();
	const q = Number(row.quantity);
	if (!Number.isFinite(q)) return 0;
	if (st === 'I' && tt === 'L') return q;
	if (st === 'R' && tt === 'O') return -q;
	return 0;
}

function rowBalanceQtyRaw(row) {
	if (!row || typeof row !== 'object') return null;
	for (const k of ['balance_qty', 'balanceQty', 'balance_quantity']) {
		if (row[k] !== undefined && row[k] !== null && row[k] !== '') return row[k];
	}
	return null;
}

function rowDateMs(row) {
	return new Date(row?.date_time || 0).getTime() || 0;
}

/** Saldo acumulado exibido: opening_qty + Σ(balance_delta) na ordem cronológica. */
export function recalcBalancesFromOpening(rows, summary, { fullSequence = false } = {}) {
	if (!rows?.length) return;
	if (fullSequence && summary?.opening_qty != null && summary.opening_qty !== '') {
		const opening = typeof summary.opening_qty === 'number'
			? summary.opening_qty
			: parseBalanceBr(summary.opening_qty);
		if (Number.isFinite(opening)) {
			const sorted = rows.slice().sort((a, b) => rowDateMs(a) - rowDateMs(b));
			let running = opening;
			const byRef = new WeakMap();
			for (const r of sorted) {
				running += parseBalanceDelta(r);
				byRef.set(r, running);
			}
			for (const r of rows) {
				r._sialbi_balance_qty = byRef.has(r)
					? byRef.get(r)
					: parseBalanceBr(rowBalanceQtyRaw(r));
			}
			return;
		}
	}
	for (const r of rows) {
		r._sialbi_balance_qty = parseBalanceBr(rowBalanceQtyRaw(r));
	}
}

/** @deprecated alias — use recalcBalancesFromOpening */
export function annotateNonNegativeBalances(rows, summary, fullSequence) {
	recalcBalancesFromOpening(rows, summary, { fullSequence: !!fullSequence });
}

/** Garante «Saldo ao fim» = estoque físico (/stocks). */
export function summaryAlignClosingWithStock(summary, stockTotals) {
	if (!summary || typeof summary !== 'object') return summary;
	const out = { ...summary };
	const tot = stockTotals?.qty_total ?? summary.current_stock_qty;
	if (tot != null && Number.isFinite(Number(tot))) {
		out.closing_qty = Number(tot);
	}
	return out;
}

/** KPIs do resumo na vista cliente — totais vêm da API; só ajusta contagem de linhas visíveis. */
export function summaryForClientView(summary, visibleRows) {
	if (!summary || typeof summary !== 'object') return summary;
	const rows = visibleRows || [];
	if (!rows.length) return { ...summary, line_count: 0 };
	return {
		...summary,
		line_count: rows.length,
	};
}

function rowDisplayBalance(row) {
	const adj = row?._sialbi_balance_qty;
	if (adj != null && Number.isFinite(Number(adj))) return Number(adj);
	return parseBalanceBr(rowBalanceQtyRaw(row));
}

/** Texto + tom visual para colunas numéricas do Kardex (grade usa textContent no td). */
export function kardexNumericCell(row, key) {
	if (key === 'qty_delta') {
		const n = parseQtyDelta(row);
		return {
			text: fmtNumberPt(row?.qty_delta),
			tone: n < 0 ? 'out' : n > 0 ? 'in' : null,
		};
	}
	if (key === 'balance_qty') {
		const n = rowDisplayBalance(row);
		return { text: fmtNumberPt(n), tone: n < 0 ? 'out' : null };
	}
	return null;
}

function locationIsAwaitingAddressing(loc) {
	const code = String(loc?.code ?? '').trim();
	const name = String(loc?.name ?? '').trim();
	return code.toUpperCase() === AWAITING_ADDRESSING_CODE
		|| name.toUpperCase() === AWAITING_ADDRESSING_CODE;
}

export function buildStagingContext(locations) {
	const stagingIds = {};
	const awaitingIds = {};
	for (const loc of locations || []) {
		if (!loc?.id) continue;
		const sid = String(loc.id);
		if (loc.is_staging) stagingIds[sid] = true;
		if (locationIsAwaitingAddressing(loc)) awaitingIds[sid] = true;
	}
	return { stagingIds, awaitingIds };
}

export function shouldExcludeStagingStock(locationId, stagingIds, awaitingIds) {
	if (locationId == null || locationId === '') return false;
	const lid = String(locationId);
	if (!stagingIds[lid]) return false;
	return !awaitingIds[lid];
}

function primaryLocationId(row, isSource) {
	const flatKeys = isSource
		? ['from_location_id', 'source_location_id', 'beginning_location_id', 'origin_location_id',
			'fromLocationId', 'sourceLocationId', 'originLocationId', 'locationSourceId']
		: ['to_location_id', 'target_location_id', 'end_location_id', 'destination_location_id',
			'toLocationId', 'targetLocationId', 'destinationLocationId', 'locationTargetId'];
	for (const k of flatKeys) {
		const v = row[k];
		if (v != null && String(v).trim() !== '') return String(v).trim();
	}
	const objKey = isSource ? 'source' : 'target';
	const o = row[objKey];
	if (o && typeof o === 'object') {
		for (const ik of ['location_id', 'locationId', 'stock_location_id', 'position_id', 'id']) {
			if (o[ik] != null && String(o[ik]).trim() !== '') return String(o[ik]).trim();
		}
	}
	return null;
}

/** Omite movimentos só entre staging genérico (exceto S-1). */
export function filterStagingOnlyMoves(rows, stagingIds, awaitingIds) {
	return (rows || []).filter((r) => {
		const s = primaryLocationId(r, true);
		const t = primaryLocationId(r, false);
		if (!s || !t) return true;
		return !(shouldExcludeStagingStock(s, stagingIds, awaitingIds)
			&& shouldExcludeStagingStock(t, stagingIds, awaitingIds));
	});
}

function locationAddressString(loc) {
	const addr = loc?.address;
	if (Array.isArray(addr)) {
		const parts = addr.map(String).map((p) => p.trim()).filter(Boolean);
		return parts.length ? parts.join('-') : '';
	}
	if (typeof addr === 'string' && addr.trim()) return addr.trim();
	return '';
}

export function locationDisplayLabel(locationId, locById, awaitingIds) {
	if (locationId == null || locationId === '') return '—';
	const lid = String(locationId);
	if (awaitingIds[lid]) return AWAITING_ADDRESSING_LABEL;
	const loc = locById[lid];
	if (!loc) return lid;
	const addr = locationAddressString(loc);
	if (addr) return addr;
	const code = String(loc.code ?? '').trim();
	const name = String(loc.name ?? '').trim();
	if (code) return code;
	if (name) return name;
	return lid;
}

/** Mapa id → endereço; busca /locations sem popup de permissão (403 = mapa vazio). */
export async function fetchLocationLabelMap(companyId, listLocationsFn) {
	const fe = translate({ 'field-0': 'company_id', 'operator-0': 'eq', 'value-0': companyId });
	const batch = 500;
	const all = [];

	async function fetchPageSilent(q) {
		if (listLocationsFn) {
			const chunk = await listLocationsFn(q);
			if (Array.isArray(chunk)) return chunk;
		}
		try {
			const res = await fetch(`${getSialApiHost()}/locations${q}`, {
				method: 'GET',
				credentials: 'include',
			});
			if (!res.ok) return [];
			const data = await res.json();
			return Array.isArray(data) ? data : [];
		} catch {
			return [];
		}
	}

	for (let offset = 0; ; offset += batch) {
		const q = `?filter=${encodeURIComponent(fe)}&limit=${batch}&offset=${offset}`;
		const chunk = await fetchPageSilent(q);
		if (!chunk.length) break;
		all.push(...chunk);
		if (chunk.length < batch) break;
	}
	const byId = {};
	for (const loc of all) {
		if (!loc?.id) continue;
		byId[String(loc.id)] = loc;
	}
	const { stagingIds, awaitingIds } = buildStagingContext(all);
	const labels = {};
	for (const loc of all) {
		if (loc?.id == null) continue;
		labels[String(loc.id)] = locationDisplayLabel(loc.id, byId, awaitingIds);
	}
	return { labels, locations: all, awaitingIds, stagingIds };
}

function translateMovKind(k) {
	if (!k) return '—';
	const s = String(k).trim();
	const low = s.toLowerCase();
	if (KDX_MOV_KIND[low]) return KDX_MOV_KIND[low];
	return s.replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function fmtLote(row) {
	if (!row) return '—';
	for (const k of ['batch_number', 'batch', 'lot_code', 'lot_label', 'lot_number']) {
		const v = row[k];
		if (v != null && String(v).trim() !== '') return String(v).trim();
	}
	if (row.lot?.lot_number) return String(row.lot.lot_number).trim();
	const lid = row.lot_id;
	if (lid != null && lid !== '' && /^\d{1,12}$/.test(String(lid))) return String(lid);
	return '—';
}

function tipoFlow(row) {
	const a = String(row?.source_type ?? '').trim().charAt(0).toUpperCase();
	const b = String(row?.target_type ?? '').trim().charAt(0).toUpperCase();
	if (!a && !b) return '—';
	if (!a) return b;
	if (!b) return a;
	return `${a} -> ${b}`;
}

function isGarbageMetadataObject(o) {
	if (!o || typeof o !== 'object' || Array.isArray(o)) return true;
	const keys = Object.keys(o);
	if (!keys.length) return true;
	const hasPoint = ['location_id', 'locationId', 'warehouse_id', 'input_id', 'output_id',
		'display_name', 'name', 'label', 'position_label'].some(
		(k) => o[k] != null && String(o[k]).trim() !== '',
	);
	return !hasPoint && keys.every((k) => ['reason', 'replenishment_id', 'inventory_id', 'company_id'].includes(k));
}

function fmtEndpointObject(o) {
	if (!o || typeof o !== 'object' || isGarbageMetadataObject(o)) return '';
	for (const p of ['display_name', 'name', 'label', 'position_label', 'reference', 'code', 'title']) {
		if (o[p] != null && String(o[p]).trim() !== '') return String(o[p]).trim();
	}
	return '';
}

function mergePointExtra(base, extra) {
	const e = String(extra ?? '').trim();
	if (!e) return base;
	const b = String(base ?? '').trim();
	if (!b || b === '—') return e;
	if (b.includes(e)) return b;
	return `${e} · ${b}`;
}

function fmtStockPoint(row, role, labelMap) {
	if (!row) return '—';
	const isSource = role === 'source';
	const apiLabel = String(row[isSource ? 'source_label' : 'target_label'] ?? '').trim();
	const flatIdKeys = isSource
		? ['source_id', 'source_location_id', 'from_location_id', 'origin_location_id', 'fromLocationId', 'sourceLocationId']
		: ['target_id', 'target_location_id', 'to_location_id', 'destination_location_id', 'toLocationId', 'targetLocationId'];
	let id = primaryLocationId(row, isSource) || '';
	if (!id) {
		for (const fk of flatIdKeys) {
			const v = row[fk];
			if (v != null && String(v).trim() !== '') { id = String(v).trim(); break; }
		}
	}
	const o = endpointObj(row, role);
	if (!id && o?.id != null) id = String(o.id).trim();
	const lb = id && labelMap?.[id] ? labelMap[id] : '';
	const objLabel = fmtEndpointObject(o);
	let out = '—';
	if (apiLabel && apiLabel !== '—') out = apiLabel;
	else if (id && lb) out = lb;
	else if (objLabel) out = objLabel;
	else if (id && lb) out = `ID ${id} · ${lb}`;
	else if (id) out = `ID ${id}`;
	else if (lb) out = lb;
	const extra = isSource
		? String(row._sialbi_origem_extra ?? '').trim()
		: String(row._sialbi_destino_extra ?? '').trim();
	return mergePointExtra(out, extra);
}

function endpointObj(row, role) {
	let o = role === 'source' ? row?.source : row?.target;
	if (typeof o === 'string') {
		try { o = JSON.parse(o); } catch { o = null; }
	}
	return o && typeof o === 'object' ? o : null;
}

function extractEntradaNfFromLabel(label) {
	const s = String(label ?? '').trim();
	const m = s.match(/^Entrada\s*·\s*NF\s+(.+)$/i);
	return m ? m[1].trim() : '';
}

function extractSaidaRefFromLabel(label) {
	const s = String(label ?? '').trim();
	const m = s.match(/Saída\s*·\s*(.+)$/i) || s.match(/Saida\s*·\s*(.+)$/i);
	if (!m) return '';
	const ref = m[1].trim();
	return ref && ref.toLowerCase() !== 'saída' && ref.toLowerCase() !== 'saida' ? ref : '';
}

function fmtNfEntrada(row) {
	const bi = String(row?._sialbi_nf_entrada ?? row?.nf_entrada ?? '').trim();
	if (bi) return bi;
	const st = String(row?.source_type ?? '').toUpperCase();
	const tt = String(row?.target_type ?? '').toUpperCase();
	if (st === 'I') {
		const fromLabel = extractEntradaNfFromLabel(row?.source_label);
		if (fromLabel) return fromLabel;
	}
	if (tt === 'I') {
		const fromLabel = extractEntradaNfFromLabel(row?.target_label);
		if (fromLabel) return fromLabel;
	}
	for (const k of ['invoice_number', 'nf_number', 'nfe_number', 'document_number', 'numero_nf']) {
		const v = row?.[k];
		if (v != null && String(v).trim() !== '') return String(v).trim();
	}
	for (const role of ['source', 'target']) {
		const o = endpointObj(row, role);
		const iid = o?.input_id;
		if (iid != null && String(iid).trim() !== '') return `Entrada #${iid}`;
	}
	const iid = row?.input_id ?? endpointObj(row, 'source')?.input_id;
	if (iid) return `Entrada #${iid}`;
	return '—';
}

function fmtReqHis(row) {
	const bi = String(row?._sialbi_requisicao_his ?? row?.requisicao_his ?? '').trim();
	if (bi) return bi;
	const st = String(row?.source_type ?? '').toUpperCase();
	const tt = String(row?.target_type ?? '').toUpperCase();
	if (st === 'R') {
		const ref = extractSaidaRefFromLabel(row?.source_label);
		if (ref) return ref;
	}
	if (tt === 'O') {
		const ref = extractSaidaRefFromLabel(row?.target_label);
		if (ref) return ref;
	}
	for (const k of ['reference', 'customer_reference', 'his_number', 'requisition_number', 'order_reference']) {
		const v = row?.[k];
		if (v != null && String(v).trim() !== '') return String(v).trim();
	}
	for (const role of ['source', 'target']) {
		const o = endpointObj(row, role);
		const oid = o?.output_id;
		if (oid != null && String(oid).trim() !== '') return `Saída #${oid}`;
	}
	return '—';
}

function fmtBloqueio(row) {
	const v = row?._sialbi_bloqueio_motivo ?? row?.bloqueio_motivo ?? row?.movement_reason;
	return v != null && String(v).trim() !== '' ? String(v).trim() : '—';
}

function fmtLoteAtendido(row) {
	const bi = String(row?._sialbi_lote_atendido ?? '').trim();
	if (bi) return bi;
	return '—';
}

/** Valor de célula para grade/export (paridade kdxCellValueForCol). */
export function cellValueForCol(row, key, rowNum, labelMap, { html = false } = {}) {
	if (key === '__num') return String(rowNum);
	if (key === '__nf_entrada_html') {
		return `<span class="kdx-kardex-destaque kdx-kardex-nf">${escHtml(fmtNfEntrada(row))}</span>`;
	}
	if (key === '__req_his_html') {
		return `<span class="kdx-kardex-destaque kdx-kardex-his">${escHtml(fmtReqHis(row))}</span>`;
	}
	if (key === '__nf_entrada') return fmtNfEntrada(row);
	if (key === '__req_his') return fmtReqHis(row);
	if (key === '__lote') return fmtLote(row);
	if (key === '__lote_atendido') return fmtLoteAtendido(row);
	if (key === '__tipo') return tipoFlow(row);
	if (key === '__bloqueio_motivo' || key === '__bloqueio_motivo_html') return fmtBloqueio(row);
	if (key === '__valor_sinal') {
		const vs = row?.value_signed;
		return vs == null || vs === '' ? '—' : fmtBRL(vs);
	}
	if (key === 'qty_delta') return fmtNumberPt(row?.qty_delta);
	if (key === 'balance_qty') return fmtNumberPt(rowDisplayBalance(row));
	if (key === 'date_time') return fmtDateTime(row?.date_time);
	if (key === 'movement_kind') return translateMovKind(row?.movement_kind);
	if (key === 'source' || key === 'target') return fmtStockPoint(row, key, labelMap);
	if (key === 'quantity' || key === 'qty_delta') return fmtNumberPt(row?.[key]);
	if (key === 'balance_value' || key === 'value_signed') return fmtBRL(row?.[key]);
	return row?.[key] == null ? '—' : String(row[key]);
}

export function exportCellPlain(row, col, rowNum, labelMap) {
	if (!col) return '';
	const k = col.key;
	if (k.endsWith('_html')) {
		const plainKey = k.replace(/_html$/, '');
		return cellValueForCol(row, plainKey === '__nf_entrada' ? '__nf_entrada' : plainKey === '__req_his' ? '__req_his' : '__bloqueio_motivo', rowNum, labelMap);
	}
	if (k === 'qty_delta') return cellValueForCol(row, 'qty_delta', rowNum, labelMap);
	return cellValueForCol(row, k, rowNum, labelMap);
}

function referenceFromOrderLike(rec) {
	for (const k of ['reference', 'order_reference', 'external_reference', 'customer_reference',
		'client_reference', 'buyer_reference', 'his_number', 'requisition_number']) {
		const v = String(rec?.[k] ?? '').trim();
		if (v) return v;
	}
	return '';
}

function collectIdsFromRow(node, inputIds, outputIds, ooIds, depth = 0) {
	if (depth > 18 || !node || typeof node !== 'object') return;
	for (const [k, v] of Object.entries(node)) {
		if (Array.isArray(v)) {
			for (const item of v) collectIdsFromRow(item, inputIds, outputIds, ooIds, depth + 1);
			continue;
		}
		if (v && typeof v === 'object') {
			const kl = k.toLowerCase();
			if (kl === 'output' && v.id) outputIds.add(Number(v.id));
			if ((kl === 'input' || kl === 'entrada') && v.id) inputIds.add(Number(v.id));
			if (kl.includes('output_order') && v.id) ooIds.add(Number(v.id));
			collectIdsFromRow(v, inputIds, outputIds, ooIds, depth + 1);
			continue;
		}
		const id = parseInt(v, 10);
		if (!id) continue;
		const kl = k.toLowerCase();
		if (/output_order/.test(kl)) ooIds.add(id);
		else if (/output_id|saida_id|outputid/.test(kl)) outputIds.add(id);
		else if (/input_id|entrada_id|inputid|nf_entrada/.test(kl)) inputIds.add(id);
	}
}

async function fetchListSilent(path, query) {
	try {
		const res = await fetch(`${getSialApiHost()}${path}${query}`, {
			method: 'GET',
			credentials: 'include',
		});
		if (!res.ok) return [];
		const data = await res.json();
		return Array.isArray(data) ? data : [];
	} catch {
		return [];
	}
}

async function fetchEntitiesByIds(listFn, ids, companyId, endpoint) {
	const byId = {};
	const unique = [...new Set(ids.filter((i) => i > 0))];
	for (let i = 0; i < unique.length; i += 12) {
		const chunk = unique.slice(i, i + 12);
		const f = `company_id*eq*${companyId},${chunk.map((id) => `id*eq*${id}`).join(';')}`;
		const q = `?limit=50&filter=${encodeURIComponent(f)}`;
		let rows;
		try {
			rows = listFn ? await listFn(q) : await fetchListSilent(`/${endpoint}`, q);
		} catch {
			rows = endpoint ? await fetchListSilent(`/${endpoint}`, q) : [];
		}
		if (!Array.isArray(rows)) {
			rows = endpoint ? await fetchListSilent(`/${endpoint}`, q) : [];
		}
		for (const row of rows) {
			if (row?.id) byId[String(row.id)] = row;
		}
	}
	return byId;
}

function entityContainsProduct(entity, productId) {
	const pid = parseInt(productId, 10);
	if (!pid || !Array.isArray(entity?.items) || !entity.items.length) return true;
	return entity.items.some((item) => Number(item?.product_id) === pid);
}

function applyRowEnrichment(row, inputById, outputById, ooById, productId) {
	const pid = parseInt(productId, 10);
	const mk = movementKindLower(row);
	const st = String(row.source_type ?? '').toUpperCase();
	const tt = String(row.target_type ?? '').toUpperCase();
	const isEntrada = mk.includes('entrada') || st === 'I';
	const isSaida = mk.includes('baixa') || mk.includes('saida') || mk.includes('saída') || tt === 'O';
	const reservaOuSep = mk.includes('reserva') || mk.includes('picking');
	const inputIds = new Set();
	const outputIds = new Set();
	const ooIds = new Set();
	collectIdsFromRow(row, inputIds, outputIds, ooIds);
	const orderedInputIds = [...inputIds].map(Number).filter(Boolean);
	const orderedOutputIds = [...outputIds].map(Number).filter(Boolean);
	const orderedOoIds = [...ooIds].map(Number).filter(Boolean);

	if (isEntrada && !row.nf_entrada && !row._sialbi_nf_entrada) {
		for (const iid of orderedInputIds) {
			const inp = inputById[String(iid)];
			if (inp && !entityContainsProduct(inp, pid)) continue;
			if (inp) {
				const inv = String(inp.invoice_number ?? inp.document_number ?? inp.nf_number ?? '').trim();
				row._sialbi_nf_entrada = inv || `Entrada #${iid}`;
				row._sialbi_origem_extra = inv ? `Entrada #${iid} · NF ${inv}` : row._sialbi_nf_entrada;
			} else {
				row._sialbi_nf_entrada = `Entrada #${iid}`;
				row._sialbi_origem_extra = row._sialbi_nf_entrada;
			}
			break;
		}
		if (!row._sialbi_nf_entrada) {
			const fromLabel = extractEntradaNfFromLabel(row?.source_label)
				|| extractEntradaNfFromLabel(row?.target_label);
			if (fromLabel) row._sialbi_nf_entrada = fromLabel;
		}
	}

	for (const oid of orderedOutputIds) {
		const out = outputById[String(oid)];
		if (!out || !entityContainsProduct(out, pid)) continue;
		const ref = referenceFromOrderLike(out);
		if ((isSaida || reservaOuSep) && !row.requisicao_his && !row._sialbi_requisicao_his) {
			row._sialbi_requisicao_his = ref || `Saída #${oid}`;
			row._sialbi_destino_extra = `Saída #${oid}${ref ? ` · ${ref}` : ''}`;
		}
		if (isSaida && Array.isArray(out.items)) {
			const it = out.items.find((x) => !pid || Number(x.product_id) === pid);
			if (it?.lot_number) row._sialbi_lote_atendido = String(it.lot_number).trim();
		}
		break;
	}

	if (!row._sialbi_requisicao_his && (isSaida || reservaOuSep)) {
		for (const ooId of orderedOoIds) {
			const oo = ooById[String(ooId)];
			if (!oo || !entityContainsProduct(oo, pid)) continue;
			const ref = referenceFromOrderLike(oo);
			row._sialbi_requisicao_his = ref || `Pedido #${ooId}`;
			break;
		}
		if (!row._sialbi_requisicao_his) {
			const ref = extractSaidaRefFromLabel(row?.source_label)
				|| extractSaidaRefFromLabel(row?.target_label);
			if (ref) row._sialbi_requisicao_his = ref;
			else {
				const oid = endpointObj(row, 'source')?.output_id ?? endpointObj(row, 'target')?.output_id;
				if (oid) row._sialbi_requisicao_his = `Saída #${oid}`;
			}
		}
	}

	if ((mk.includes('bloqueio') || mk.includes('bloquear')) && !row._sialbi_bloqueio_motivo) {
		for (const bk of ['block_reason', 'motivo_bloqueio', 'reason', 'motivo']) {
			const v = String(row[bk] ?? '').trim();
			if (v) { row._sialbi_bloqueio_motivo = v; break; }
		}
	}
}

/** Enriquecimento NF/HIS/lote (paridade kardex_enrich.php). */
export async function enrichKardexRows(rows, companyId, productId, { listInputs, listOutputs, listOutputOrders }) {
	if (!rows?.length) return;
	const inputIds = new Set();
	const outputIds = new Set();
	const ooIds = new Set();
	for (const r of rows) collectIdsFromRow(r, inputIds, outputIds, ooIds);

	const [inputById, outputById, ooById] = await Promise.all([
		fetchEntitiesByIds(listInputs, [...inputIds], companyId, 'inputs'),
		fetchEntitiesByIds(listOutputs, [...outputIds], companyId, 'outputs'),
		fetchEntitiesByIds(listOutputOrders, [...ooIds], companyId, 'output-orders'),
	]);

	for (const r of rows) {
		applyRowEnrichment(r, inputById, outputById, ooById, productId);
	}
}

function stockDedupeKey(s) {
	if (s?.id != null && String(s.id).trim() !== '') return `id:${s.id}`;
	return `lot:${s.lot_id ?? ''}|loc:${s.location_id ?? ''}|wh:${s.warehouse_id ?? ''}`;
}

async function mergeStocksFilter(companyId, filter, listStocksFn, byKey, maxPages = 40) {
	let offset = 0;
	const limit = 500;
	for (let p = 0; p < maxPages; p++) {
		const q = `?sort=1&limit=${limit}&offset=${offset}&filter=${encodeURIComponent(filter)}`;
		const chunk = await listStocksFn(q);
		if (!Array.isArray(chunk) || !chunk.length) break;
		for (const s of chunk) {
			if (!s || typeof s !== 'object') continue;
			const rowCid = String(s.company_id ?? '').trim();
			if (rowCid && rowCid !== companyId) continue;
			byKey[stockDedupeKey(s)] = s;
		}
		if (chunk.length < limit) break;
		offset += limit;
	}
}

async function lotIdsForProduct(companyId, productId, listLotsFn) {
	const seen = new Set();
	let offset = 0;
	const limit = 500;
	const f = `company_id*eq*${companyId},product_id*eq*${productId}`;
	for (let page = 0; page < 80; page++) {
		const q = `?sort=1&limit=${limit}&offset=${offset}&filter=${encodeURIComponent(f)}`;
		const chunk = await listLotsFn(q);
		if (!Array.isArray(chunk) || !chunk.length) break;
		for (const lot of chunk) {
			if (lot?.id != null) seen.add(String(lot.id));
		}
		if (chunk.length < limit) break;
		offset += limit;
	}
	return [...seen];
}

/** Rodapé estoque atual (paridade sialbi_kardex_stock_totals_for_product). */
export async function stockTotalsForProduct(companyId, productId, { listStocks, listLots }) {
	const byKey = {};
	await mergeStocksFilter(companyId, `company_id*eq*${companyId},product_id*eq*${productId}`, listStocks, byKey);
	const lotIds = await lotIdsForProduct(companyId, productId, listLots);
	for (let i = 0; i < lotIds.length; i += 28) {
		const chunk = lotIds.slice(i, i + 28);
		const parts = chunk.map((lid) => `lot_id*eq*${lid}`).join(';');
		await mergeStocksFilter(companyId, `company_id*eq*${companyId},${parts}`, listStocks, byKey);
	}
	let livre = 0;
	let bloq = 0;
	let total = 0;
	for (const s of Object.values(byKey)) {
		const qa = Number(s.quantity_available) || 0;
		const qr = Number(s.quantity_reserved) || 0;
		const qu = Number(s.quantity_unsettled) || 0;
		if (qa <= 0 && qr <= 0 && qu <= 0) continue;
		livre += qa;
		bloq += qu;
		total += qa + qr + qu;
	}
	return { qty_livre: livre, qty_bloqueado: bloq, qty_total: total };
}

export function summaryDisplayItems(summary, { companyName, productLabel, dateFimInclusive } = {}) {
	if (!summary || typeof summary !== 'object') return [];
	const items = [];
	const add = (label, value) => {
		if (value === null || value === undefined || value === '') return;
		items.push({ label, value: String(value) });
	};
	if (companyName) add('Empresa', companyName);
	if (productLabel) add('Produto', productLabel);
	if (summary.date_from != null) add('Data inicial do período', fmtDateOnly(summary.date_from));
	if (dateFimInclusive) {
		add('Data final do período (inclusiva)', fmtDateOnly(dateFimInclusive));
	} else if (summary.date_to_exclusive != null) {
		add('Data final do período', fmtDateOnly(summary.date_to_exclusive));
	}
	const keys = [
		['period_entries_qty', 'Entradas no período (un.)'],
		['period_exits_qty', 'Saídas / consumos no período (un.)'],
		['line_count', 'Linhas no extrato'],
		['opening_qty', 'Saldo no início do período'],
		['closing_qty', 'Saldo ao fim do período'],
	];
	for (const [k, label] of keys) {
		if (summary[k] != null && summary[k] !== '') add(label, fmtNumberPt(summary[k]));
	}
	return items;
}

/** Aplica classe de coluna estreita para textos longos (origem, NF, etc.). */
export function kardexCellClass(colKey) {
	return KDX_REF_COL_KEYS.has(colKey) ? 'kx-cell-ref' : '';
}
